import { createPortal } from 'react-dom';
import { Button, Icon } from '../ui';

interface NoteRequirementsAlertProps {
  open: boolean;
  templateName: string;
  /** Phrases from unmetRequirements(). */
  missing: string[];
  onClose: () => void;
}

/**
 * Confirm in a spec queue panel, when the picked canned note asks for
 * something the job doesn't have yet. Nothing was queued; closing returns to
 * the panel with the note and files intact so the human adds what's missing
 * and confirms again. Sits above every SidePanel layer.
 */
export default function NoteRequirementsAlert({ open, templateName, missing, onClose }: NoteRequirementsAlertProps) {
  if (!open) return null;

  return createPortal(
    <div className="ck-overlay-root">
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'var(--surface-overlay)' }} />
      <section
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="note-requirements-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          zIndex: 201,
          width: '420px',
          maxWidth: '92vw',
          transform: 'translate(-50%, -50%)',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-pop)',
          padding: '20px 22px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--status-warn-soft)',
              color: 'var(--status-warn)',
              flexShrink: 0,
            }}
          >
            <Icon name="paperclip" size={16} />
          </span>
          <div id="note-requirements-title" style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text-body)' }}>
            Not queued yet
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 1.5, color: 'var(--text-muted)' }}>
          The “{templateName}” note needs this before it can go to the spec queue:
        </p>
        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-sm)', lineHeight: 1.6, color: 'var(--text-body)' }}>
          {missing.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', lineHeight: 1.5, color: 'var(--text-faint)' }}>
          Add it in the panel, then confirm again. Switching the canned note to “No note” drops the requirement.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} autoFocus>
            Add a file
          </Button>
        </div>
      </section>
    </div>,
    document.body
  );
}
